import { Box } from "@mui/material";
import * as React from "react";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { ConfirmationDialogResult, DatabaseRecordType, TaskHandlerType, TaskLockingType, TaskRepetitionType, TaskStateEditabilityType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import * as taskService from "../../../models/services/taskService";
import { ISessionState } from "../../../models/session/sessionState";
import * as storeActions from "../../../models/store/storeActions";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { DialogTabs } from "../../framework/Dialog/DialogTabs";
import { TaskEditDialogGeneraView } from "./TaskEditDialogGeneraView";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { ITaskDataRow, SaveTaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskEditItem } from "source/models/task/taskEditItem";
import { ITaskEditItemTaskState } from "source/models/task/taskEditItemTaskState";
import { ITaskEditItemTaskPriority } from "source/models/task/taskEditItemTaskPriority";
import { ITaskDataRowCol, SaveTaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { recordLockManager } from "source/models/services/recordLockService";
import { StoreUtils } from "source/models/store/storeUtils";
import { TaskDataRowList } from "../TaskDataRowList/TaskDataRowList";
import { TaskEditDialogFormView } from "./TaskEditDialogFormView";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { archiveConfirmationWord, removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { Base } from "ui/Scripts/source/framework/base";
import { TaskNewDialog } from "../TaskNewDialog";
import { TaskEditDialogGeneralViewReadOnly } from "./TaskEditDialogGeneralViewReadOnly";
import { CalendarTaskListOfRecord, getOpenCalendarTaskAmount } from "source/components/calendarTask/CalendarTaskListOfRecord";
import { FormAccordion } from "source/components/framework/FormAccordion";
import { EditToggleButton } from "source/components/framework/Button/EditToggleButton";
import { ITaskHandler, SaveTaskHandler } from "source/models/task/taskHandler";
import { IToggleButtonOption } from "source/components/framework/ToggleButtons";

/* eslint-disable no-unused-vars */
export const enum TaskEditDialogMenuItem {
    General = "1",
    Remainders = "2"
}
/* eslint-enable no-unused-vars */

export interface ITaskEditDialogProp {
    sessionState: ISessionState;
    companyId: string;
    taskId: string;
    classes?: string;
    hasPrevious: boolean;
    hasNext: boolean;
    onOk: (id: string) => void;
    onCancel: () => void;
    onPrint: () => void;
    //onNewSaved: (userGroupId: string) => void;
    onRefreshList: () => Promise<void>;
    onPrevious: () => void;
    onNext: () => void;
    //onRefreshSessionState: () => void;
}

export interface ITaskEditDialogState {
    activeMenuItem: TaskEditDialogMenuItem;
    isLoading: boolean;
    taskStateIsReadOnly: boolean;
    generalViewEditMode: boolean;
    task: ITaskEditItem;
    id: string;
    rowId: string;
    code: string;
    name: string;
    taskTypeId: string;
    taskStateId: string;
    taskPriorityId: string;
    unitId: string;
    taskHeaderDataCols: ITaskDataRowCol[];
    taskDataRows: ITaskDataRow[];
    handlers: ITaskHandler[];
    taskType: ITaskEditTypeItem;
    visibilityOptions: IToggleButtonOption[];
    canUpdateTask: boolean;
    canRemoveTask: boolean;
    canChangeTaskState: boolean;
    canChangeTaskHeaderData: boolean;
    allTaskTypes: ITaskEditTypeItem[];
    taskTypes: ITaskEditTypeItem[];
    taskStates: ITaskEditItemTaskState[];
    taskPriorities: ITaskEditItemTaskPriority[];
    units: IIdTitleNode[];
    taskTypeUnitIds: string[];
    lockedUser: IModifyUser;
    isLockedToOtherUser: boolean;
    showNewTaskDialog: boolean;
    listHeight: string;
    openCalendarTaskAmount: number;
    rowToolButtons: React.ReactNode;
}

export class TaskEditDialog extends React.Component<ITaskEditDialogProp, ITaskEditDialogState> {
    private _isMounted: boolean;
    private generalViewContainerDiv: HTMLDivElement;
    private orgStateHash: string = "";

    constructor(props: ITaskEditDialogProp) {
        super(props);
        this.state = {
            activeMenuItem: TaskEditDialogMenuItem.General,
            isLoading: false,
            taskStateIsReadOnly: false,
            generalViewEditMode: props.taskId !== Base.emptyGuid,
            task: null,
            id: props.taskId,
            rowId: "",
            code: "",
            name: "",
            taskStateId: "",
            taskTypeId: "",
            taskPriorityId: "",
            unitId: "",
            taskHeaderDataCols: [],
            taskDataRows: [],
            handlers: [],
            taskType: null,
            visibilityOptions: [],
            canUpdateTask: false,
            canRemoveTask: false,
            canChangeTaskState: false,
            canChangeTaskHeaderData: false,
            allTaskTypes: [],
            taskTypes: [],
            taskStates: [],
            taskPriorities: [],
            units: [],
            taskTypeUnitIds: [],
            lockedUser: null,
            isLockedToOtherUser: false,
            showNewTaskDialog: false,
            listHeight: null,
            openCalendarTaskAmount: 0,
            rowToolButtons: null
        };
    }

    setOpenTaskAmount = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(() => getOpenCalendarTaskAmount(props.companyId, DatabaseRecordType.Task, id), false, true);
        this.setState({ openCalendarTaskAmount: result });
    };

    loadTask = async (id: string) => {
        const props = this.props;
        //Unlock current task
        const unlockRecord = this.state.canUpdateTask || this.state.canChangeTaskState;
        if (unlockRecord && LockedRecord.isLockedToUser(this.state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(props.companyId, this.state.id, DatabaseRecordType.Task);
        }
        //Load and lock task
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => taskService.getTaskEdit(props.companyId, id, true), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const task = result.item;
        const isNew = task.id === Base.emptyGuid;
        const taskType = result.taskTypes.find(i => i.id === task.taskTypeId);
        const taskState = result.taskStates.find(i => i.id === task.taskStateId);
        const visibilityOptions = TaskNewDialog.getVisibilityOptions(result.taskTypes);
        const state = {
            ...this.state,
            isLocked: taskState?.editabilityType === TaskStateEditabilityType.ReadOnly,
            task,
            id: task.id,
            rowId: task.rowId,
            code: task.code,
            name: task.name,
            taskStateId: task.taskStateId,
            taskTypeId: task.taskTypeId,
            taskPriorityId: task.taskPriorityId,
            unitId: task.unitId,
            taskHeaderDataCols: task.taskHeaderDataCols,
            taskDataRows: task.taskDataRows,
            handlers: task.handlers,
            taskType,
            visibilityOptions,
            canUpdateTask: result.canUpdateTask,
            canRemoveTask: result.canRemoveTask,
            canChangeTaskState: result.canChangeTaskState,
            canChangeTaskHeaderData: result.canChangeTaskHeaderData,
            allTaskTypes: result.taskTypes.slice(0),
            taskTypes: isNew
                ? TaskEditTypeItem.getTaskTypesByVisibility(result.taskTypes, !!task.unitId)
                : result.taskTypes,
            taskStates: result.taskStates,
            taskPriorities: result.taskPriorities,
            units: result.units,
            taskTypeUnitIds: result.taskTypeUnitIds,
            lockedUser: task.lockedUser,
            isLockedToOtherUser: task.isLockedToOtherUser(props.sessionState.code),
            showNewTaskDialog: isNew,
            taskStateIsReadOnly: taskState?.editabilityType === TaskStateEditabilityType.ReadOnly,
            activeMenuItem: isNew
                ? TaskEditDialogMenuItem.General
                : this.state.activeMenuItem,
            generalViewEditMode: isNew
        };
        this.setState(state);
        const saveData = this.getSaveDataFromState(props, state);
        this.orgStateHash = saveData.hash;
        //Calendar task amount
        if (!isNew) {
            this.setOpenTaskAmount(id);
        }
        //Add locked record to idb
        const lockRecord = state.canUpdateTask || state.canChangeTaskState;
        if (!isNew && lockRecord && LockedRecord.isLockedToUser(task.lockedUser, props.sessionState.code)) {
            recordLockManager.addLock(LockedRecord.createLockedRecord(props.companyId, state.id, props.sessionState.code, DatabaseRecordType.Task));
        }
        //Send read receipt for task
        if (!isNew) {
            const userIsCreator = task.createUser.code === props.sessionState.code;
            if (!userIsCreator && !task.isRead) {
                AppUtils.callService(() => taskService.sendTaskReadReceipt(props.companyId, state.id), false, true);
            }
        }
    };

    getMaxListHeight = (): string => {
        if (!this.generalViewContainerDiv) return "calc(100vh - 330px)";
        const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
        const height = this.generalViewContainerDiv.getBoundingClientRect().height;
        return viewportHeight - 330 - height > 100
            ? "calc(100vh - " + (330 + height).toString(10) + "px)"
            : "calc(100vh - 330px)";
    };
    
    getMaxListHeightExpanded = (): string => {
        return "calc(100vh - 180px)";
    };

    private observer: ResizeObserver;

    createObserver = () => {
        this.observer = new ResizeObserver((entries) => {
            if (!this._isMounted) return;
            this.setState({
                listHeight: this.getMaxListHeight()
            });
        });
    };

    setObserver = () => {
        if (this.state.activeMenuItem === TaskEditDialogMenuItem.General && this.generalViewContainerDiv) {
            this.observer.observe(this.generalViewContainerDiv);
        }
    };

    removeObserver = () => {
        if (!this.observer) return;
        this.observer.disconnect();
    };

    componentDidMount(): void {
        this._isMounted = true;
        this.loadTask(this.props.taskId);
        this.createObserver();
        this.setObserver();
    }

    async componentWillUnmount(): Promise<void> {
        this._isMounted = false;
        const props = this.props;
        const state = this.state;
        const isNew = state.id === Base.emptyGuid;
        const lockRecord = state.canUpdateTask || state.canChangeTaskState;
        if (!isNew && lockRecord && LockedRecord.isLockedToUser(this.state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.Task);
        }
        this.removeObserver();
    }

    async componentDidUpdate(prevProps: ITaskEditDialogProp, prevState: ITaskEditDialogState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId && prevProps.taskId === props.taskId) {
            if (state.listHeight !== this.getMaxListHeight()) {
                this.removeObserver();
                this.setObserver();
            }
            return;
        }
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb()) return;
            props.onRefreshList();
        }
        await this.loadTask(props.taskId);
    }

    handleToggleActiveMenuItem = (activeMenuItem: TaskEditDialogMenuItem) => {
        this.setState({ activeMenuItem });
    };

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "xts") {
            this.setState({ code: value });
        } else if (name === "name") {
            this.setState({ name: value });
        }
    };

    getChangeTaskTypeStateChange = (taskTypeId: string) => {
        const state = this.state;
        const taskType = state.taskTypes.find(i => i.id === taskTypeId);
        if (!taskType) return null;
        const taskHeaderDataCols = TaskDataRowCol.initializeValuesByType(taskType.headerCols, []);
        const taskDataRows: ITaskDataRow[] = [];
        if (taskType.taskRepetitionType === TaskRepetitionType.NotRepeated) {
            const taskDataRow = new TaskDataRow();
            taskDataRow.id = Base.getGuid();
            taskDataRow.initializeToTaskType(taskType);
            taskDataRows.push(taskDataRow);
        }
        return {
            taskTypeId,
            taskType,
            taskHeaderDataCols,
            taskDataRows,
        };
    };

    getTaskPrioritiesAndTaskStatesBasedOnTaskType = async (taskTypeId: string, defaultUnitId: string) => {
        const state = this.state;
        const result = await AppUtils.callService(() => taskService.getTaskEditTaskTypeData(this.props.companyId, taskTypeId), false, true);
        if (!result) return;
        const taskPriority = result.taskPriorities.find(i => i.id === state.taskPriorityId);
        const taskState = result.taskStates.find(i => i.id === state.taskStateId);
        this.setState({
            taskPriorities: result.taskPriorities,
            taskStates: result.taskStates,
            taskPriorityId: taskPriority?.id ?? result.taskPriorities[0].id,
            taskStateId: taskState?.id ?? result.taskStates[0].id,
            taskTypeUnitIds: result.taskTypeUnitIds
        });
        //Change unit based on task type
        const oldUnitId = defaultUnitId ?? state.unitId;
        let unitId = oldUnitId;
        if (!!oldUnitId && result.taskTypeUnitIds.length > 0) {
            unitId = result.taskTypeUnitIds.find(i => i === oldUnitId) || result.taskTypeUnitIds[0];
        }
        if (oldUnitId === unitId) return;
        this.handleChangeUnit(unitId);
    };

    handleChangeTaskType = (taskTypeId: string, defaultUnitId?: string) => {
        const stateChange = this.getChangeTaskTypeStateChange(taskTypeId);
        if (!stateChange) return;
        this.setState(stateChange);
        this.getTaskPrioritiesAndTaskStatesBasedOnTaskType(taskTypeId, defaultUnitId);
    };

    getTaskTypesBasedOnUnit = async (unitId: string) => {
        if (!unitId) return;
        const state = this.state;
        const result = await AppUtils.callService(() => taskService.getTaskEditUnitData(this.props.companyId, unitId), false, true);
        if (!result) return;
        const taskType = result.taskTypes.find(i => i.id === state.taskTypeId);
        this.setState({
            taskTypes: result.taskTypes
        });
        const taskTypeId = taskType?.id ?? result.taskTypes[0].id;
        if (state.taskTypeId === taskTypeId) return;
        this.handleChangeTaskType(taskTypeId);
    };

    handleChangeUnit = (unitId: string) => {
        if (unitId === this.state.unitId) return;
        this.setState({
            unitId,
        });
        if (!this.state.task.isNew() || !unitId) return;
        this.getTaskTypesBasedOnUnit(unitId);
    };

    // #region Locking
    handleUnlockAndLockToMe = async () => {
        const props = this.props;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToUnlockTask, this.state.task.name), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const result = await recordLockManager.lock(props.companyId, this.state.id, props.sessionState.code, DatabaseRecordType.Task);
        if (!result || result.items.length < 1) return;
        this.setState({
            lockedUser: StoreUtils.getNowModifyUser(result.items[0].lockedDate),
            isLockedToOtherUser: false
        });
    };
    // #endregion Locking

    // #region TaskNewDialog
    handleTaskNewDialogOk = (unitId: string, taskTypeId: string, taskStateId: string, taskPriorityId: string, taskTypes: ITaskEditTypeItem[], taskStates: ITaskEditItemTaskState[], taskPriorities: ITaskEditItemTaskPriority[], taskTypeUnitIds: string[]) => {
        const stateChange = this.getChangeTaskTypeStateChange(taskTypeId);
        if (!stateChange) return;
        this.setState({
            ...stateChange,
            unitId,
            taskStateId,
            taskPriorityId,
            showNewTaskDialog: false,
            taskTypes,
            taskStates,
            taskPriorities,
            taskTypeUnitIds
        });
    };
    
    handleTaskNewDialogCancel = () => {
        this.props.onCancel();
    };
    // #endregion TaskNewDialog

    getSaveDataFromState = (props: ITaskEditDialogProp, state: ITaskEditDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("rowId", state.rowId);
        result.append("companyId", props.companyId);
        result.append("id", state.id);
        result.append("code", state.code);
        result.append("name", state.name);
        result.append("taskPriorityId", state.taskPriorityId);
        result.append("taskStateId", state.taskStateId);
        result.append("taskTypeId", state.taskTypeId);
        result.append("unitId", state.unitId ? state.unitId : null);
        result.append("taskHeaderDataCols", state.taskType.headerCols && state.taskType.headerCols.length
            ? JSON.stringify(TaskDataRowCol.getToBeSavedTaskDataRowCols(state.taskType.headerCols, state.taskHeaderDataCols).map(i => new SaveTaskDataRowCol(i)))
            : null);
        result.append("taskDataRow", state.taskType.taskRepetitionType === TaskRepetitionType.NotRepeated && state.taskDataRows.length > 0
            ? JSON.stringify(SaveTaskDataRow.createSaveTaskDataRow(state.taskType.formCols, state.taskDataRows[0], null))
            : null
        );
        result.append("saveTaskHandlers", state.task.isNew()
            ? state.handlers.map(i => new SaveTaskHandler(i))
            : null
        );
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.name) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.TitleMustBeDefined));
                return resolve(false);
            }
            if (state.taskType.headerCols && state.taskType.headerCols.length) {
                TaskTypeCol.validateInputOfRequiredTaskTypeCols(state.taskType.headerCols, state.taskHeaderDataCols, null,
                    (typeCol: ITaskTypeCol) => {
                        AppUtils.showErrorMessage(String.format(Translations.FieldParameterValueMustBeDefined, typeCol.title));
                        return resolve(false);
                    });
            }
            if (state.taskType.taskRepetitionType === TaskRepetitionType.NotRepeated) {
                for (let i = 0; i < state.taskDataRows.length; i++) {
                    TaskTypeCol.validateInputOfRequiredTaskTypeCols(state.taskType.formCols, state.taskDataRows[i].cols, null,
                        (typeCol: ITaskTypeCol) => {
                            AppUtils.showErrorMessage(String.format(Translations.FieldParameterValueMustBeDefined, typeCol.title));
                            return resolve(false);
                        });
                }
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => taskService.saveTaskEdit(saveData.obj));
        if (!result) return null;
        //Update state and hash
        const stateChange = { id: result.id, rowId: result.rowId };
        this.setState(stateChange);
        this.orgStateHash = this.getSaveDataFromState(props, { ...state, ...stateChange }).hash;
        return result;
    };

    handleSave = async (closeDialog: boolean) => {
        const props = this.props;
        const result = await this.saveToDb();
        if (!result) return;
        if (!closeDialog) {
            this.loadTask(result.id);
            props.onOk(result.id);
            return;
        }
        props.onCancel();
    };

    handleMoveToTrashBin = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isLockedToOtherUser || state.taskStateIsReadOnly || state.task.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToMoveTaskParameterToTrashBin, state.task.name), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => taskService.moveTaskToTrashBin(props.companyId, state.task.id));
            if (!result) return;
            props.onCancel();
        }
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isLockedToOtherUser || state.taskStateIsReadOnly || state.task.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveTaskParameterPermanenty, state.task.name), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => taskService.removeTask(props.companyId, state.task.id));
            if (!result) return;
            this.setState({ lockedUser: null }, () => props.onCancel());
        }
    };

    handleArchive = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isLockedToOtherUser || state.taskStateIsReadOnly || state.task.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToArchiveTaskParameter, state.task.name), false, archiveConfirmationWord) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => taskService.archiveTask(props.companyId, state.task.id));
            if (!result) return;
            this.setState({ lockedUser: null }, () => props.onCancel());
        }
    };

    handleRestoreFromArchive = async () => {
        const props = this.props;
        const state = this.state;
        if (state.isLockedToOtherUser || state.taskStateIsReadOnly || state.task.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRestoreTaskParameterFromArchive, state.task.name), false) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => taskService.restoreTaskFromArchive(props.companyId, state.task.id));
            if (!result) return;
            this.loadTask(state.task.id);
        }
    };

    handlePrint = async () => {
        const props = this.props;
        const state = this.state;
        if (state.task.isNew()) return;
        props.onPrint();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onCancel();
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getSaveDataFromState(props, this.state).hash, this.orgStateHash,
            async () => !!await this.saveToDb(),
            props.onRefreshList,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.task) return null;
        //New dialog
        if (state.showNewTaskDialog) {
            return (
                <TaskNewDialog
                    companyId={props.companyId}
                    units={state.units}
                    taskTypes={state.taskTypes}
                    taskPriorities={state.taskPriorities}
                    taskStates={state.taskStates}
                    taskTypeUnitIds={state.taskTypeUnitIds}
                    unitId={state.unitId}
                    taskTypeId={state.taskTypeId}
                    taskStateId={state.taskStateId}
                    taskPriorityId={state.taskPriorityId}
                    onOk={this.handleTaskNewDialogOk}
                    onCancel={this.handleTaskNewDialogCancel}
                />
            );
        }
        //Edit dialog
        const isNew = state.task.isNew();
        const isArchived = !!state.task.archiveUser;
        const isRepeated = state.taskType.taskRepetitionType === TaskRepetitionType.Repeated;
        const savedFormIsLocked = !isNew && state.taskType.taskLockingType === TaskLockingType.LockSavedForm;
        const canSaveTask = state.canUpdateTask || state.canChangeTaskState;
        const hasPersonsInCharge = !isNew && state.handlers.filter(i => i.taskHandlerType === TaskHandlerType.PersonInCharge).length > 0;
        const isReadOnly = !(canSaveTask && !isArchived);
        return (
            <Dialog
                id="taskEditDialog"
                maxWidth="lg"
                maxHeight="100%"
                title={isNew
                    ? Translations.AddTask
                    : `${Translations.EditTask}: ${state.task.code}, ${state.task.name}${(state.task.archiveUser ? ", " + Translations.Archived : "")}${(isReadOnly ? ", " + Translations.ReadOnly : "")}${(state.isLockedToOtherUser ? ", " + Translations.Locked : "")}`
                }
                dialogTabs={
                    <DialogTabs
                        activeTabId={state.activeMenuItem}
                        tabs={[
                            { id: TaskEditDialogMenuItem.General, title: Translations.BasicInformation },
                            { id: TaskEditDialogMenuItem.Remainders, title: Translations.CalendarTasks, badgeContent: state.openCalendarTaskAmount },
                        ]}
                        onChangeTab={this.handleToggleActiveMenuItem}
                    />
                }
                body={
                    <>
                        {state.activeMenuItem === TaskEditDialogMenuItem.General &&
                            <>
                                <div ref={(elem) => { this.generalViewContainerDiv = elem; }}>
                                    <FormAccordion
                                        title={Translations.Task}
                                        buttonsOnExpanded={!isNew && (state.canChangeTaskHeaderData || state.canChangeTaskState) && !isArchived
                                            ? <EditToggleButton
                                                value="edit"
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    this.setState({ generalViewEditMode: !state.generalViewEditMode });
                                                }}
                                                selected={state.generalViewEditMode}
                                            />
                                            : null
                                        }
                                        detailsPadding="8px 0 8px"
                                        defaultExpanded={true}
                                        onExpandedChange={() => {
                                            this.setState({
                                                listHeight: this.getMaxListHeight()
                                            });
                                        }}
                                    >
                                        {(state.generalViewEditMode
                                            ? <TaskEditDialogGeneraView
                                                sessionState={props.sessionState}
                                                isNew={isNew}
                                                taskStateIsReadOnly={state.taskStateIsReadOnly}
                                                isLockedToOtherUser={state.isLockedToOtherUser}
                                                companyId={props.companyId}
                                                taskId={state.id}
                                                name={state.name}
                                                taskTypeId={state.taskTypeId}
                                                taskStateId={state.taskStateId}
                                                taskPriorityId={state.taskPriorityId}
                                                unitId={state.unitId}
                                                createUser={state.task.createUser}
                                                taskHandlers={state.handlers}
                                                taskType={state.taskType}
                                                canChangeTaskHeaderData={state.canChangeTaskHeaderData}
                                                canChangeTaskState={state.canChangeTaskState}
                                                taskHeaderDataCols={state.taskHeaderDataCols}
                                                allTaskTypes={state.allTaskTypes}
                                                taskTypes={state.taskTypes}
                                                taskStates={state.taskStates}
                                                taskPriorities={state.taskPriorities}
                                                units={state.units}
                                                visibilityOptions={state.visibilityOptions}
                                                taskTypeUnitIds={state.taskTypeUnitIds}
                                                onChangeName={(name: string) => this.setState({ name })}
                                                onChangeHandlers={(handlers: ITaskHandler[]) => this.setState({ handlers })}
                                                onChangeUnit={this.handleChangeUnit}
                                                onChangeTaskTypes={(taskTypes: ITaskEditTypeItem[]) => this.setState({ taskTypes })}
                                                onChangeTaskType={this.handleChangeTaskType}
                                                onChangeTaskState={(taskStateId: string) => this.setState({ taskStateId })}
                                                onChangeTaskPriority={(taskPriorityId: string) => this.setState({ taskPriorityId })}
                                                onChangeTaskHeaderDataCols={(taskHeaderDataCols: ITaskDataRowCol[]) => this.setState({ taskHeaderDataCols })}
                                            />
                                            : <TaskEditDialogGeneralViewReadOnly
                                                companyId={props.companyId}
                                                name={state.name}
                                                isArchived={isArchived}
                                                taskStateId={state.taskStateId}
                                                taskPriorityId={state.taskPriorityId}
                                                unitId={state.unitId}
                                                createUser={state.task.createUser}
                                                taskHandlers={state.handlers}
                                                taskType={state.taskType}
                                                taskHeaderDataCols={state.taskHeaderDataCols}
                                                taskStates={state.taskStates}
                                                taskPriorities={state.taskPriorities}
                                                units={state.units}
                                            />
                                        )}
                                    </FormAccordion>
                                </div>
                                <FormAccordion
                                    title={isRepeated ? Translations.TaskDataRows : Translations.Contents}
                                    detailsPadding="8px 0 0"
                                    defaultExpanded={true}
                                    expanendedMargin="0"
                                    buttonsOnExpanded={isRepeated
                                        ? state.rowToolButtons
                                        : null
                                    }
                                >
                                    {isRepeated &&
                                        <TaskDataRowList
                                            sessionState={props.sessionState}
                                            isReadOnly={state.taskStateIsReadOnly || !state.canUpdateTask || isNew || isArchived}
                                            companyId={props.companyId}
                                            taskId={state.id}
                                            dataId=""
                                            taskType={state.taskType}
                                            height={state.listHeight}
                                            onSetToolButtons={(rowToolButtons) => this.setState({ rowToolButtons })}
                                        />
                                    }
                                    {!isRepeated &&
                                        <TaskEditDialogFormView
                                            isNew={isNew}
                                            isLockedToOtherUser={state.isLockedToOtherUser}
                                            isReadOnly={state.taskStateIsReadOnly || !state.canUpdateTask || savedFormIsLocked || isArchived}
                                            companyId={props.companyId}
                                            taskId={state.id}
                                            taskType={state.taskType}
                                            taskDataRows={state.taskDataRows}
                                            onChangeTaskDataRows={(taskDataRows: ITaskDataRow[]) => this.setState({ taskDataRows })}
                                        />
                                    }
                                </FormAccordion>
                            </>
                        }
                        {state.activeMenuItem === TaskEditDialogMenuItem.Remainders &&
                            <CalendarTaskListOfRecord
                                sessionState={props.sessionState}
                                isReadOnly={state.taskStateIsReadOnly || !state.canUpdateTask || isNew || isArchived}
                                infoText={state.unitId ? Translations.TaskCalendarTasksInfo : undefined}
                                warningText={!state.unitId || hasPersonsInCharge ? undefined : Translations.TaskCalendarTasksPersonsInChargeWarning}
                                companyId={props.companyId}
                                recordId={state.id}
                                ownerRecordTitle={Translations.Task + ": " + state.task.name}
                                dataId=""
                                databaseRecordType={DatabaseRecordType.Task}
                                noDataMessage={Translations.TaskMustBeSavedBeforeCalendarTasksCanBeAdded}
                                onSetOpenCalendarTaskAmount={(openCalendarTaskAmount) => this.setState({ openCalendarTaskAmount })}
                            />
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                            createUser={!isNew ? state.task.createUser : null}
                            modifyUser={!isNew ? state.task.modifyUser : null}
                            archiveUser={!isNew ? state.task.archiveUser : null}
                            deleteUser={!isNew ? state.task.deleteUser : null}
                            lockedUser={state.lockedUser}
                            isLockedToOtherUser={!isNew && state.isLockedToOtherUser}
                            onUnlockAndLockToMe={props.sessionState.isSuperAdmin ? this.handleUnlockAndLockToMe : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            removeDisabled={state.isLockedToOtherUser || state.taskStateIsReadOnly || !state.canRemoveTask}
                            saveDisabled={state.isLockedToOtherUser}
                            removeTitle={state.task.deleteUser ? Translations.RemoveTaskPermanently : null}
                            onCancel={this.handleCancel}
                            onPrint={!isNew ? this.handlePrint : null}
                            onRemove={!isNew && state.canUpdateTask ? (state.task.deleteUser ? this.handleRemove : this.handleMoveToTrashBin) : null}
                            onArchive={!isNew && !state.task.deleteUser && !state.task.archiveUser && state.canRemoveTask ? this.handleArchive : null}
                            onRestoreFromArchive={!isNew && !state.task.deleteUser && state.task.archiveUser && state.canRemoveTask ? this.handleRestoreFromArchive : null}
                            onSave={canSaveTask && !isArchived ? () => this.handleSave(false) : null}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}